import { z } from 'zod';

/** The slice of a TanStack route this reads. */
export type RouteForSearch = {
  options?: { validateSearch?: unknown };
  parentRoute?: unknown;
};

/**
 * The query-param names a Zod search schema accepts, or `null` when that
 * can't be known: a hand-written validator function, or an object that keeps
 * unknown keys (`.loose()` / `.catchall()`). Wrappers (optional, default,
 * catch, transform pipes) are looked through to the object inside.
 */
export function schemaKeys(schema: unknown): string[] | null {
  if (schema instanceof z.ZodObject) {
    const { catchall } = schema.def;
    if (catchall && !(catchall instanceof z.ZodNever)) return null;
    return Object.keys(schema.shape);
  }
  if (schema instanceof z.ZodPipe) return schemaKeys(schema.in);
  if (
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodDefault ||
    schema instanceof z.ZodCatch ||
    schema instanceof z.ZodReadonly
  ) {
    return schemaKeys(schema.def.innerType);
  }
  return null;
}

function isRoute(value: unknown): value is RouteForSearch {
  return typeof value === 'object' && value !== null;
}

/**
 * Every query param a route accepts: TanStack Router validates a URL's
 * search against the route's own `validateSearch` and each ancestor's, so the
 * accepted names are their union. `null` when any of them can't be read, so
 * nothing is wrongly rejected.
 */
export function acceptedSearchParams(route: RouteForSearch): string[] | null {
  const keys = new Set<string>();
  let current: unknown = route;
  while (isRoute(current)) {
    const validate = current.options?.validateSearch;
    if (validate !== undefined) {
      const own = schemaKeys(validate);
      if (own === null) return null;
      own.forEach((key) => keys.add(key));
    }
    current = current.parentRoute;
  }
  return [...keys].sort();
}
